<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            ajax-path="rhipe/microsoftcsp/tenants"
        />
        <app-modal
            :show.sync="showSubscriptionsModal"
            width="lg:w-3/4"
            custom-class="flex flex-col h-full"
        >
            <template slot="header">{{ selected.CustomerName }} Subscriptions</template>
            <template slot="body">
                <tabulator
                    :columns="subscriptionsColumns"
                    :actions="subscriptionsActions"
                    ajax-path="rhipe/microsoftcsp/subscriptions"
                    :ajax-params="{tenant_id: selected.TenantId}"
                />
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Http from '@/helpers/Http';
    import Vue from 'vue';

    export default {
        components: {
            KeyboardArrowRight: () => import('@/icons/KeyboardArrowRight.vue'),
        },
        data() {
            return {
                refreshTable: false,
                tableIsLoading: false,
                tableLoadingText: null,
                showSubscriptionsModal: false,
                selected: {},
                columns: [
                    {
                        title: 'Customer Name',
                        field: 'CustomerName',
                        sorter: 'string',
                        minWidth: 270,
                    },
                    {
                        title: 'Domain',
                        field: 'TenantDomain',
                        sorter: 'string',
                        minWidth: 300,
                    },
                    {
                        title: 'Program Name',
                        field: 'ProgramName',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Status',
                        field: 'TenantStatus',
                        sorter: 'string',
                        minWidth: 100,
                    },
                ],
                subscriptionsColumns: [
                    {
                        title: 'Name',
                        field: 'FriendlyName',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Users',
                        field: 'Quantity',
                        sorter: 'string',
                        minWidth: 150,
                        formatter({_cell}) {
                            let Component = Vue.extend({
                                template: `
                                    <div class="flex items-center">
                                        <app-button 
                                            class="btn-red btn-sm" 
                                            @click="decrement"
                                            :disable="incrementing || counter === 0"
                                            :loading="decrementing"
                                        >-</app-button>
                                        <input 
                                            type="text" 
                                            v-model="counter" 
                                            class="mx-2 px-2 py-1 rounded w-10 border shadow bg-white"
                                            disabled
                                        />
                                        <app-button 
                                            class="bg-green-500 hover:bg-green-600 btn-sm text-white"
                                            @click="increment"
                                            :disable="decrementing"
                                            :loading="incrementing"
                                        >+</app-button>
                                    </div>
                                `,
                                data() {
                                    return {
                                        counter: _cell.value,
                                        incrementing: false,
                                        decrementing: false,
                                    }
                                },
                                methods: {
                                    increment() {                                        
                                        this.incrementing = true;
                                        Http.post('rhipe/microsoftcsp/subscriptions/updateQuantity', {
                                                subscriptionId: _cell.row.data.SubscriptionId,
                                                quantity: this.counter + 1,
                                            })
                                            .then(({ data }) => {
                                                this.incrementing = false;
                                                this.counter++;
                                            })
                                            .catch(({ response }) => {
                                                this.incrementing = false;
                                            })
                                    },
                                    decrement() {
                                        this.decrementing = true;                                        
                                        Http.post('rhipe/microsoftcsp/subscriptions/updateQuantity', {
                                                quantity: this.counter - 1,
                                                subscriptionId: _cell.row.data.SubscriptionId,
                                            })
                                            .then(({ data }) => {
                                                this.decrementing = false;
                                                this.counter--;
                                            })
                                            .catch(({ response }) => {
                                                this.decrementing = false;
                                            })
                                    }
                                }
                            });
                            
                            return new Component().$mount().$el;
                        }
                    },
                    {
                        title: 'Status',
                        field: 'Status',
                        sorter: 'string',
                        minWidth: 130,
                        width: 130,
                        widthGrow: false,
                        formatter({_cell}, params) {
                            if (_cell.value == 'Active') {
                                return `<span class='badge badge-green'>Active</span>`;
                            }

                            return `<span class='badge badge-red'>${_cell.value}</span>`;
                        }
                    },
                ],
                subscriptionsActions: [
                    {
                        label: 'Resume',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        handler: subscriptions => {
                            this.resumeSubscription(subscriptions[0]);
                        },
                        isEligible(subscriptions) {
                            return subscriptions[0].Status == 'Suspended';
                        }
                    },
                    {
                        label: 'Suspend',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        handler: subscriptions => {
                            this.suspendSubscription(subscriptions[0]);
                        },
                        isEligible(subscriptions) {
                            return subscriptions[0].Status == 'Active';
                        }
                    },
                ],
                actions: [
                    {
                        label: 'Subscriptions',
                        icon: () => import('@/icons/MonetizationOnOutlined.vue'),
                        handler: (data) => {
                            this.selected = data[0];
                            this.showSubscriptionsModal = true;
                        }
                    },
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    },
                ]
            }
        },
        methods: {
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                Http.patch('rhipe/microsoftcsp/tenants/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
            resumeSubscription(subscription) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to resume <strong>${subscription.FriendlyName}</strong> subscription.`,
                    showCancelButton: true,
                    confirmButtonColor: '#3182ce',
                    confirmButtonText: 'Yes, resume it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`rhipe/microsoftcsp/subscriptions/${subscription.SubscriptionId}/resume`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Subscription was successfully resumed.'
                        })
                    }
                })
            },
            suspendSubscription(subscription) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to suspend <strong>${subscription.FriendlyName}</strong> subscription.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, suspend it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`rhipe/microsoftcsp/subscriptions/${subscription.SubscriptionId}/suspend`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Subscription was successfully suspended.'
                        })
                    }
                })
            }
        }        
    }
</script>